import { URIPrefix } from '../enums/url-prefix.enum';
import {
  CityNamesByStateSlug,
  IInternalDirectoryLinkGroup,
  InsuranceTitlesByCityByStateSlug,
  InsuranceTitlesByStateSlug,
} from '../models/internal-directory-link.interface';

// Exact directory filter values; urlValue is set where the slug differs from the label.
export const INTERNAL_LINK_CONCERNS: IInternalDirectoryLinkGroup = {
  heading: 'Concerns',
  items: [
    { label: 'Anxiety', prefix: URIPrefix.SPECIALTY },
    { label: 'Depression', prefix: URIPrefix.SPECIALTY },
    { label: 'Stress', prefix: URIPrefix.SPECIALTY },
    { label: 'ADHD', prefix: URIPrefix.SPECIALTY },
    { label: 'Trauma', prefix: URIPrefix.SPECIALTY },
    { label: 'Bipolar Disorder', prefix: URIPrefix.SPECIALTY },
    { label: 'PTSD', prefix: URIPrefix.SPECIALTY },
    { label: 'OCD (Obsessive-Compulsive)', prefix: URIPrefix.SPECIALTY },
    { label: 'Intimate / Romantic Relationships', prefix: URIPrefix.SPECIALTY },
    { label: 'Addiction', prefix: URIPrefix.SPECIALTY },
  ],
};

export const INTERNAL_LINK_TREATMENT_APPROACHES: IInternalDirectoryLinkGroup = {
  heading: 'Treatment Approaches',
  items: [
    { label: 'EMDR', prefix: URIPrefix.TREATMENT_PREFERENCE },
    { label: 'Cognitive Behavioral Therapy (CBT)', prefix: URIPrefix.TREATMENT_PREFERENCE },
    { label: 'Dialectical Behavioral Therapy (DBT)', prefix: URIPrefix.TREATMENT_PREFERENCE },
    { label: 'Somatic', prefix: URIPrefix.TREATMENT_PREFERENCE },
    { label: 'Motivational interviewing', prefix: URIPrefix.TREATMENT_PREFERENCE },
    { label: 'Psychodynamic', prefix: URIPrefix.TREATMENT_PREFERENCE },
    { label: 'Internal Family Systems', prefix: URIPrefix.TREATMENT_PREFERENCE },
    { label: 'Psychoanalytic', prefix: URIPrefix.TREATMENT_PREFERENCE },
    { label: 'Acceptance & commitment therapy', prefix: URIPrefix.TREATMENT_PREFERENCE },
    { label: 'Art therapy', prefix: URIPrefix.TREATMENT_PREFERENCE },
  ],
};

export const INTERNAL_LINK_POPULAR_FILTERS: IInternalDirectoryLinkGroup = {
  heading: 'Popular Filters',
  items: [
    { label: 'Female', prefix: URIPrefix.GENDER, urlValue: 'woman' },
    { label: 'Male', prefix: URIPrefix.GENDER, urlValue: 'man' },
    { label: 'Online', prefix: URIPrefix.SESSION_TYPE, urlValue: 'video' },
    { label: 'In-person', prefix: URIPrefix.SESSION_TYPE },
    { label: 'Individual', prefix: URIPrefix.MODALITY },
    { label: 'Couples', prefix: URIPrefix.MODALITY },
    { label: 'Family', prefix: URIPrefix.MODALITY },
    { label: 'Group', prefix: URIPrefix.MODALITY },
    { label: 'Teens', prefix: URIPrefix.SPECIALTY },
    { label: 'Psychiatrists', prefix: URIPrefix.MODALITY, urlValue: 'psychiatric', isProviderType: true },
  ],
};

export const INTERNAL_LINK_STATIC_GROUPS: IInternalDirectoryLinkGroup[] = [
  INTERNAL_LINK_CONCERNS,
  INTERNAL_LINK_TREATMENT_APPROACHES,
  INTERNAL_LINK_POPULAR_FILTERS,
];

// Curated carriers per state, using exact directory filter names so each slug resolves.
export const INTERNAL_LINK_STATE_INSURANCES: InsuranceTitlesByStateSlug = {
  ak: [
    'Aetna', 'Premera Blue Cross of Alaska',
    'Cigna', 'United Healthcare',
    'Medicare', 'Moda Marketplace Health Plans',
    'Tricare Prime Active Duty HMO',
  ],
  al: ['Aetna', 'BCBS Alabama (or other BCBS)', 'Cigna', 'United Healthcare', 'Medicare', 'VIVA Health'],
  ar: ['Aetna', 'Cigna', 'United Healthcare', 'Medicare', 'Tricare Prime Active Duty HMO'],
  az: [
    'Aetna', 'BCBS Arizona (or other BCBS)',
    'Cigna', 'United Healthcare',
    'Medicare', 'Medical Mutual',
    'Imperial Health Plans', 'Medica',
    'Oscar Health Plan', 'Tricare Prime Active Duty HMO',
  ],
  ca: [
    'Aetna', 'Cigna',
    'United Healthcare', 'Ventura County Health Care Plan',
    'Western Health Advantage', 'Sharp Health Plan',
    'Tricare Prime Active Duty HMO',
  ],
  co: [
    'Aetna', 'Anthem Colorado (or other Anthem/BCBS)',
    'Cigna', 'Kaiser Permanente',
    'United Healthcare', 'Medicare',
    'Medical Mutual', 'Select Health',
    'Denver Health Medical Plan', 'Rocky Mountain Health Plan',
  ],
  ct: [
    'Aetna', 'Anthem Connecticut (or other Anthem/BCBS)',
    'Cigna', 'United Healthcare',
    'Medicare', 'Connecticare',
  ],
  dc: [
    'Aetna', 'CareFirst DC (or other BCBS)',
    'Cigna', 'Kaiser Permanente',
    'United Healthcare', 'Medicare',
    'Medical Mutual',
  ],
  de: ['Aetna', 'Highmark Blue Cross Delaware', 'Cigna', 'United Healthcare', 'Medicare'],
  fl: [
    'Aetna', 'Florida Blue (or other BCBS)',
    'Cigna', 'United Healthcare',
    'Medicare', 'AvMed',
    'Oscar Health Plan', 'Molina Marketplace',
  ],
  ga: [
    'Aetna', 'Anthem Blue Cross Blue Shield Georgia (or other BCBS plan)',
    'Cigna', 'Kaiser Permanente',
    'United Healthcare', 'Medicare',
    'Medical Mutual', 'CareSource',
    'Oscar', 'Alliant Health Plans',
  ],
  hi: [
    'Aetna', 'Blue Cross Hawaii Medical Service Association',
    'Cigna', 'United Healthcare',
    'Medicare', 'Tricare Prime Active Duty HMO',
    'Hawaii Medical Assurance Association',
  ],
  ia: [
    'Aetna', 'Blue Cross Blue Shield Wellmark (or other BCBS plan)',
    'Cigna', 'United Healthcare',
    'Medicare', 'Medica',
    'Oscar', 'Tricare Prime Active Duty HMO',
  ],
  id: [
    'Aetna', 'Regence of Idaho (or other BCBS)',
    'Cigna', 'United Healthcare',
    'Medicare', 'Moda Marketplace Health Plans',
    'Select Health', 'St. Luke\'s Health Plan',
    'Tricare Prime Active Duty HMO',
  ],
  il: [
    'Aetna', 'BCBS Illinois (or other BCBS) PPO Plans Only',
    'Cigna', 'United Healthcare',
    'Medicare', 'Oscar Health Plan',
    'Tricare Prime Active Duty HMO',
  ],
  in: [
    'Aetna', 'Anthem Indiana (or other Anthem/BCBS)',
    'Cigna', 'United Healthcare',
    'Medicare', 'Medical Mutual',
    'CareSource',
  ],
  ks: [
    'Aetna', 'BCBS Kansas City (or other BCBS)',
    'Cigna', 'United Healthcare',
    'Medicare', 'Medica',
    'Oscar Health Plan', 'Tricare Prime Active Duty HMO',
  ],
  ky: ['Aetna', 'Anthem Kentucky (or other Anthem/BCBS)', 'Cigna', 'United Healthcare', 'Medicare', 'Medical Mutual'],
  la: [
    'Aetna', 'BCBS Louisana (or other BCBS)',
    'Cigna', 'United Healthcare',
    'Medicare', 'Tricare Prime Active Duty HMO',
  ],
  ma: [
    'Aetna', 'Blue Cross Blue Shield Massachusetts',
    'Cigna', 'United Healthcare',
    'Medicare', 'Medical Mutual',
    'Tufts Health Plan', 'Harvard Pilgrim Health Plan',
    'Mass General Brigham Health Plan',
  ],
  md: [
    'Aetna', 'CareFirst Maryland (or other BCBS)',
    'Cigna', 'Kaiser Permanente',
    'United Healthcare', 'Medicare',
    'Medical Mutual',
  ],
  me: [
    'Aetna', 'Anthem Maine (or other Anthem/BCBS)',
    'Cigna', 'United Healthcare',
    'Medicare', 'Medical Mutual',
    'Tufts', 'Harvard Pilgrim Health Plan',
    'Community Health Options', 'Martin\'s Point US Family Health Plan',
  ],
  mi: ['Aetna', 'Blue Cross Blue Shield Michigan', 'Cigna', 'United Healthcare', 'Medicare', 'Paramount', 'Priority Health'],
  mn: ['Aetna', 'Cigna', 'United Healthcare', 'Medicare', 'Tricare Prime Active Duty HMO'],
  mo: [
    'Aetna', 'Anthem Missouri (or other Anthem/BCBS)',
    'Cigna', 'United Healthcare',
    'Medicare', 'Medical Mutual',
    'Oscar Health Plan', 'Medica',
    'Tricare Prime Active Duty HMO', 'Ambetter Home State Health',
  ],
  ms: ['Aetna', 'Cigna', 'United Healthcare', 'Medicare'],
  mt: [
    'Aetna', 'BCBS Montana (or other BCBS)',
    'Cigna', 'United Healthcare',
    'Medicare', 'Mountain Health CO-OP',
    'Tricare Prime Active Duty HMO', 'ClaimDOC',
  ],
  nc: [
    'Aetna', 'Blue Cross Blue Shield North Carolina (or other BCBS plan)',
    'Cigna', 'United Healthcare',
    'Medicare', 'Oscar',
    'ClaimDOC', 'Partners Direct',
  ],
  nd: ['Aetna', 'Cigna', 'United Healthcare', 'Medicare'],
  ne: [
    'Aetna', 'BCBS Nebraska (or other BCBS)',
    'Cigna', 'United Healthcare',
    'Medicare', 'Medical Mutual',
    'Oscar Health Plan', 'Medica',
    'Tricare Prime Active Duty HMO', 'Partners Direct',
  ],
  nh: [
    'Aetna', 'Anthem New Hampshire (or other Anthem/BCBS)',
    'Cigna', 'United Healthcare',
    'Medicare', 'Medical Mutual',
    'Tufts Health Plan', 'Harvard Pilgrim Health Plan',
    'ClaimDOC',
  ],
  nj: [
    'Aetna', 'Horizon Blue Cross Blue Shield New Jersey',
    'Cigna', 'United Healthcare',
    'Medicare', 'Oscar Health Plan',
    'ClaimDOC',
  ],
  nm: [
    'Aetna', 'BCBS New Mexico (or other BCBS)',
    'Cigna', 'United Healthcare',
    'Medicare', 'Presbyterian Health Plan',
    'Tricare Prime Active Duty HMO', 'Prime Health',
  ],
  nv: [
    'Aetna', 'Anthem Blue Cross and Blue Shield Nevada',
    'Cigna', 'United Healthcare',
    'Medicare', 'Imperial Health Plans',
    'Tricare Prime Active Duty HMO', 'Hometown Health',
    'Aetna Allied Benefit System', 'Aetna Meritain',
  ],
  ny: [
    'Aetna', 'Cigna',
    'United Healthcare', 'Oscar Health Plan',
    'MVP Healthcare', 'ClaimDOC',
    'Partners Direct', 'Unity Preferred Network',
  ],
  oh: [
    'Aetna', 'Anthem Ohio (or other Anthem/BCBS)',
    'Cigna', 'United Healthcare',
    'Medicare', 'Paramount',
    'OhioHealthy', 'CareSource',
    'The Health Plan', 'Medical Mutual of Ohio',
  ],
  ok: [
    'Aetna', 'BCBS Oklahoma (or other BCBS)',
    'Cigna', 'United Healthcare',
    'Medicare', 'Medica',
    'Oscar Health Plan', 'Tricare Prime Active Duty HMO',
    'Alliance Coal Health Plan', 'Imagine Health',
  ],
  or: [
    'Aetna', 'Regence of Oregon (or other BCBS)',
    'Cigna', 'United Healthcare',
    'Medicare', 'Moda Marketplace Health Plans',
    'Tricare Prime Active Duty HMO', 'First Choice Health',
  ],
  pa: [
    'Aetna', 'Capital Blue Cross (or other BCBS plan)',
    'Highmark (or other BCBS plan)', 'Cigna',
    'United Healthcare', 'Medicare',
    'The Health Plan', 'Oscar',
    'Partners Direct', 'Prime Health',
  ],
  ri: ['Aetna', 'BCBS Rhode Island (or other BCBS)', 'Cigna', 'United Healthcare', 'Medicare'],
  sc: [
    'Aetna', 'Cigna',
    'United Healthcare', 'Medicare',
    'Aetna Allied Benefit System', 'Aetna Gravie',
    'Aetna Meritain', 'ClaimDOC',
    'Sana Benefits', 'Prime Health',
  ],
  sd: ['Aetna', 'Cigna', 'United Healthcare', 'Medicare', 'Medica', 'Tricare Prime Active Duty HMO'],
  tn: [
    'Aetna', 'Blue Cross Blue Shield Tennessee (or other BCBS plan)',
    'Cigna', 'United Healthcare',
    'Medicare', 'Oscar',
    'Partners Direct', 'ClaimDOC',
    'Prime Health',
  ],
  tx: [
    'Aetna', 'Blue Cross Blue Shield of Texas PPO (or other BCBS plan)',
    'Cigna', 'United Healthcare',
    'Medicare', 'Medical Mutual',
    'Moda Health', 'Imperial Health Plans',
    'HealthPartners', 'Texicare',
  ],
  ut: [
    'Aetna', 'Regence of Utah (or other BCBS)',
    'Cigna', 'United Healthcare',
    'Medicare', 'Imperial Health Plans',
    'Select Health', 'Tricare Prime Active Duty HMO',
    'Aetna Allied Benefit System', 'Aetna Meritain',
  ],
  va: [
    'Aetna', 'Anthem Blue Cross Blue Shield Virginia (or other BCBS plan)',
    'CareFirst Blue Cross Blue Shield (or other BCBS plan)', 'Cigna',
    'Kaiser Permanente', 'United Healthcare',
    'Medicare', 'Medical Mutual',
    'Oscar', 'Sentara Health Plans',
  ],
  vt: [
    'Aetna', 'BCBS Vermont (or other BCBS)',
    'Cigna', 'United Healthcare',
    'Medicare', 'Medical Mutual',
    'Harvard Pilgrim Health Plan', 'Tufts',
    'MVP Healthcare',
  ],
  wa: [
    'Aetna', 'Premera of Washington (or other BCBS)',
    'Regence of Washington (or other BCBS)', 'Cigna',
    'United Healthcare', 'Medicare',
    'Moda Marketplace Health Plans', 'Tricare Prime Active Duty HMO',
    'First Choice Health',
  ],
  wi: [
    'Aetna', 'Cigna',
    'United Healthcare', 'Medicare',
    'Network Health', 'Medica',
    'Tricare Prime Active Duty HMO', 'Sana Benefits',
  ],
  wv: [
    'Aetna', 'Cigna',
    'United Healthcare', 'Medicare',
    'The Health Plan', 'Alliance Coal Health Plan',
  ],
  wy: ['Aetna', 'Cigna', 'United Healthcare', 'Medicare', 'Mountain Health CO-OP', 'Tricare Prime Active Duty HMO'],
};

// Per-city insurance (same curated carriers), keyed state slug -> city.
export const INTERNAL_LINK_CITY_INSURANCES: InsuranceTitlesByCityByStateSlug = {
  al: {
    Montgomery: ['Aetna', 'Cigna'],
    Birmingham: ['Aetna', 'Cigna'],
    Huntsville: ['Aetna', 'BCBS Alabama (or other BCBS)', 'Cigna', 'United Healthcare'],
    Mobile: ['Aetna', 'Cigna'],
  },
  az: {
    Phoenix: [
      'Aetna', 'BCBS Arizona (or other BCBS)',
      'Cigna', 'United Healthcare',
      'Medicare', 'Imperial Health Plans',
      'Medical Mutual', 'Medica',
      'Oscar Health Plan', 'Tricare Prime Active Duty HMO',
    ],
    Tucson: [
      'Aetna', 'BCBS Arizona (or other BCBS)',
      'Cigna', 'United Healthcare',
      'Medicare', 'Imperial Health Plans',
      'Medical Mutual', 'Medica',
      'Oscar Health Plan', 'Tricare Prime Active Duty HMO',
    ],
    Mesa: [
      'Aetna', 'BCBS Arizona (or other BCBS)',
      'Cigna', 'United Healthcare',
      'Medicare', 'Imperial Health Plans',
      'Medical Mutual', 'Medica',
      'Oscar Health Plan', 'Tricare Prime Active Duty HMO',
    ],
    Chandler: [
      'Aetna', 'BCBS Arizona (or other BCBS)',
      'Cigna', 'United Healthcare',
      'Medicare', 'Imperial Health Plans',
      'Medical Mutual', 'Medica',
      'Oscar Health Plan', 'Tricare Prime Active Duty HMO',
    ],
    Scottsdale: [
      'Aetna', 'BCBS Arizona (or other BCBS)',
      'Cigna', 'United Healthcare',
      'Medicare', 'Imperial Health Plans',
      'Medical Mutual', 'Medica',
      'Tricare Prime Active Duty HMO', 'Oscar Health Plan',
    ],
    Surprise: [
      'Aetna', 'BCBS Arizona (or other BCBS)',
      'Cigna', 'United Healthcare',
      'Imperial Health Plans', 'Medical Mutual',
      'Oscar Health Plan', 'Medica',
      'Tricare Prime Active Duty HMO',
    ],
    Flagstaff: [
      'Aetna', 'BCBS Arizona (or other BCBS)',
      'Cigna', 'United Healthcare',
      'Imperial Health Plans', 'Medical Mutual',
      'Medica',
    ],
    Peoria: [
      'Aetna', 'BCBS Arizona (or other BCBS)',
      'Cigna', 'United Healthcare',
      'Imperial Health Plans', 'Medical Mutual',
      'Medica', 'Oscar Health Plan',
      'Tricare Prime Active Duty HMO',
    ],
    Tempe: ['Aetna', 'Cigna', 'Medicare', 'Imperial Health Plans', 'Medical Mutual'],
    Gilbert: [
      'Aetna', 'BCBS Arizona (or other BCBS)',
      'Cigna', 'United Healthcare',
      'Imperial Health Plans', 'Medical Mutual',
      'Medica', 'Tricare Prime Active Duty HMO',
    ],
  },
  ca: {
    'San Diego': [
      'Aetna', 'Cigna',
      'United Healthcare', 'Ventura County Health Care Plan',
      'Western Health Advantage', 'Tricare Prime Active Duty HMO',
      'Sharp Health Plan',
    ],
    'Los Angeles': [
      'Aetna', 'Cigna',
      'United Healthcare', 'Sharp Health Plan',
      'Tricare Prime Active Duty HMO', 'Ventura County Health Care Plan',
      'Western Health Advantage',
    ],
    Oakland: ['Aetna', 'Cigna'],
    'Long Beach': ['Aetna', 'Cigna'],
    Sacramento: ['Aetna', 'Cigna'],
    Corona: ['Aetna', 'Cigna'],
    Riverside: ['Aetna', 'Cigna'],
  },
  co: {
    Denver: [
      'Aetna', 'Anthem Colorado (or other Anthem/BCBS)',
      'Cigna', 'Kaiser Permanente',
      'United Healthcare', 'Medicare',
      'Medical Mutual', 'Denver Health Medical Plan',
      'Select Health', 'Rocky Mountain Health Plan',
    ],
    'Colorado Springs': [
      'Aetna', 'Anthem Colorado (or other Anthem/BCBS)',
      'Cigna', 'Kaiser Permanente',
      'United Healthcare', 'Medicare',
      'Denver Health Medical Plan', 'Medical Mutual',
      'Select Health', 'Rocky Mountain Health Plan',
    ],
    Aurora: [
      'Aetna', 'Anthem Colorado (or other Anthem/BCBS)',
      'Cigna', 'Kaiser Permanente',
      'United Healthcare', 'Medicare',
      'Medical Mutual', 'Select Health',
      'Denver Health Medical Plan', 'Tricare Prime Active Duty HMO',
    ],
    Boulder: [
      'Aetna', 'Anthem Colorado (or other Anthem/BCBS)',
      'Cigna', 'Kaiser Permanente',
      'United Healthcare', 'Medicare',
      'Select Health', 'Denver Health Medical Plan',
      'Medical Mutual', 'Rocky Mountain Health Plan',
    ],
    'Fort Collins': [
      'Aetna', 'Anthem Colorado (or other Anthem/BCBS)',
      'Cigna', 'Kaiser Permanente',
      'United Healthcare', 'Medicare',
      'Denver Health Medical Plan', 'Medical Mutual',
      'Select Health', 'Rocky Mountain Health Plan',
    ],
    Lakewood: [
      'Aetna', 'Anthem Colorado (or other Anthem/BCBS)',
      'Cigna', 'Kaiser Permanente',
      'United Healthcare', 'Medicare',
      'Denver Health Medical Plan', 'Select Health',
      'Medical Mutual', 'Rocky Mountain Health Plan',
    ],
    Littleton: [
      'Aetna', 'Anthem Colorado (or other Anthem/BCBS)',
      'Cigna', 'Kaiser Permanente',
      'United Healthcare', 'Medicare',
      'Denver Health Medical Plan', 'Medical Mutual',
      'Select Health', 'Rocky Mountain Health Plan',
    ],
    Thornton: [
      'Aetna', 'Anthem Colorado (or other Anthem/BCBS)',
      'Cigna', 'Kaiser Permanente',
      'United Healthcare', 'Medicare',
      'Denver Health Medical Plan', 'Medical Mutual',
      'Select Health', 'Rocky Mountain Health Plan',
    ],
    Centennial: [
      'Aetna', 'Anthem Colorado (or other Anthem/BCBS)',
      'Cigna', 'Kaiser Permanente',
      'United Healthcare', 'Medicare',
      'Denver Health Medical Plan', 'Medical Mutual',
      'Select Health', 'Rocky Mountain Health Plan',
    ],
    Broomfield: [
      'Aetna', 'Anthem Colorado (or other Anthem/BCBS)',
      'Cigna', 'Kaiser Permanente',
      'United Healthcare', 'Medicare',
      'Denver Health Medical Plan', 'Medical Mutual',
      'Select Health', 'Rocky Mountain Health Plan',
    ],
  },
  dc: {
    Washington: [
      'Aetna', 'CareFirst DC (or other BCBS)',
      'Cigna', 'Kaiser Permanente',
      'United Healthcare', 'Medicare',
      'Medical Mutual',
    ],
  },
  de: {
    Wilmington: ['Aetna', 'Cigna'],
  },
  fl: {
    Miami: [
      'Aetna', 'Florida Blue (or other BCBS)',
      'Cigna', 'United Healthcare',
      'Medicare', 'AvMed',
      'Oscar Health Plan', 'Molina Marketplace',
    ],
    Jacksonville: [
      'Aetna', 'Florida Blue (or other BCBS)',
      'Cigna', 'United Healthcare',
      'Medicare', 'AvMed',
      'Oscar Health Plan', 'Molina Marketplace',
    ],
    Tampa: [
      'Aetna', 'Florida Blue (or other BCBS)',
      'Cigna', 'United Healthcare',
      'Medicare', 'AvMed',
      'Oscar Health Plan', 'Molina Marketplace',
    ],
    'Fort Lauderdale': [
      'Aetna', 'Florida Blue (or other BCBS)',
      'Cigna', 'United Healthcare',
      'Medicare', 'AvMed',
      'Oscar Health Plan',
    ],
    'West Palm Beach': [
      'Aetna', 'Florida Blue (or other BCBS)',
      'Cigna', 'United Healthcare',
      'AvMed', 'Oscar Health Plan',
      'Molina Marketplace',
    ],
    Orlando: [
      'Aetna', 'Florida Blue (or other BCBS)',
      'Cigna', 'United Healthcare',
      'AvMed', 'Oscar Health Plan',
      'Molina Marketplace',
    ],
    'Boynton Beach': [
      'Aetna', 'Florida Blue (or other BCBS)',
      'Cigna', 'United Healthcare',
      'AvMed', 'Oscar Health Plan',
      'Molina Marketplace',
    ],
    Hollywood: [
      'Aetna', 'Florida Blue (or other BCBS)',
      'Cigna', 'United Healthcare',
      'Medicare', 'AvMed',
      'Oscar Health Plan', 'Molina Marketplace',
    ],
    Miramar: [
      'Aetna', 'Florida Blue (or other BCBS)',
      'Cigna', 'United Healthcare',
      'Medicare', 'AvMed',
      'Oscar Health Plan',
    ],
    'Boca Raton': [
      'Aetna', 'Florida Blue (or other BCBS)',
      'Cigna', 'United Healthcare',
      'Medicare', 'AvMed',
      'Oscar Health Plan',
    ],
  },
  ga: {
    Atlanta: [
      'Aetna', 'Anthem Blue Cross Blue Shield Georgia (or other BCBS plan)',
      'Cigna', 'Kaiser Permanente',
      'United Healthcare', 'Medicare',
      'Medical Mutual', 'CareSource',
      'Oscar', 'Alliant Health Plans',
    ],
    Marietta: [
      'Aetna', 'Anthem Blue Cross Blue Shield Georgia (or other BCBS plan)',
      'Cigna', 'Kaiser Permanente',
      'United Healthcare', 'Medicare',
      'CareSource', 'Medical Mutual',
      'Oscar', 'Alliant Health Plans',
    ],
    Lawrenceville: [
      'Aetna', 'Anthem Blue Cross Blue Shield Georgia (or other BCBS plan)',
      'Cigna', 'Kaiser Permanente',
      'United Healthcare', 'CareSource',
      'Medical Mutual', 'Oscar',
      'Alliant Health Plans',
    ],
    Mcdonough: [
      'Aetna', 'Anthem Blue Cross Blue Shield Georgia (or other BCBS plan)',
      'Cigna', 'Kaiser Permanente',
      'United Healthcare', 'CareSource',
      'Medical Mutual', 'Oscar',
      'Alliant Health Plans',
    ],
    Decatur: [
      'Aetna', 'Anthem Blue Cross Blue Shield Georgia (or other BCBS plan)',
      'Cigna', 'Kaiser Permanente',
      'United Healthcare', 'CareSource',
      'Medical Mutual', 'Oscar',
      'Alliant Health Plans',
    ],
    Douglasville: [
      'Aetna', 'Anthem Blue Cross Blue Shield Georgia (or other BCBS plan)',
      'Cigna', 'Kaiser Permanente',
      'United Healthcare', 'CareSource',
      'Medical Mutual', 'Oscar',
      'Alliant Health Plans',
    ],
    Snellville: [
      'Aetna', 'Anthem Blue Cross Blue Shield Georgia (or other BCBS plan)',
      'Cigna', 'Kaiser Permanente',
      'United Healthcare', 'CareSource',
      'Medical Mutual', 'Oscar',
      'Alliant Health Plans',
    ],
    'Stone Mountain': [
      'Aetna', 'Anthem Blue Cross Blue Shield Georgia (or other BCBS plan)',
      'Cigna', 'Kaiser Permanente',
      'United Healthcare', 'Medical Mutual',
      'CareSource', 'Oscar',
      'Alliant Health Plans',
    ],
    Savannah: [
      'Aetna', 'Anthem Blue Cross Blue Shield Georgia (or other BCBS plan)',
      'Cigna', 'Kaiser Permanente',
      'United Healthcare', 'CareSource',
      'Medical Mutual', 'Oscar',
      'Alliant Health Plans',
    ],
    Conyers: [
      'Aetna', 'Anthem Blue Cross Blue Shield Georgia (or other BCBS plan)',
      'Cigna', 'Kaiser Permanente',
      'United Healthcare', 'CareSource',
      'Medical Mutual', 'Oscar',
      'Alliant Health Plans',
    ],
  },
  il: {
    Chicago: [
      'Aetna', 'BCBS Illinois (or other BCBS) PPO Plans Only',
      'Cigna', 'United Healthcare',
      'Medicare', 'Oscar Health Plan',
      'Tricare Prime Active Duty HMO',
    ],
    Evanston: ['Aetna', 'BCBS Illinois (or other BCBS) PPO Plans Only', 'Cigna'],
    'Oak Park': ['Aetna', 'BCBS Illinois (or other BCBS) PPO Plans Only', 'Cigna'],
  },
  in: {
    Indianapolis: [
      'Aetna', 'Anthem Indiana (or other Anthem/BCBS)',
      'Cigna', 'United Healthcare',
      'Medicare', 'CareSource',
      'Medical Mutual',
    ],
    'Fort Wayne': [
      'Aetna', 'Anthem Indiana (or other Anthem/BCBS)',
      'Cigna', 'United Healthcare',
      'Medicare', 'CareSource',
      'Medical Mutual',
    ],
    Evansville: [
      'Aetna', 'Anthem Indiana (or other Anthem/BCBS)',
      'Cigna', 'United Healthcare',
      'CareSource', 'Medical Mutual',
    ],
    Lafayette: [
      'Aetna', 'Anthem Indiana (or other Anthem/BCBS)',
      'Cigna', 'United Healthcare',
      'CareSource', 'Medical Mutual',
    ],
    'South Bend': [
      'Aetna', 'Anthem Indiana (or other Anthem/BCBS)',
      'Cigna', 'United Healthcare',
      'CareSource', 'Medical Mutual',
    ],
    Valparaiso: [
      'Aetna', 'Anthem Indiana (or other Anthem/BCBS)',
      'Cigna', 'United Healthcare',
      'CareSource', 'Medical Mutual',
    ],
    Carmel: [
      'Aetna', 'Anthem Indiana (or other Anthem/BCBS)',
      'Cigna', 'United Healthcare',
      'CareSource', 'Medical Mutual',
    ],
    Jeffersonville: [
      'Aetna', 'Anthem Indiana (or other Anthem/BCBS)',
      'Cigna', 'United Healthcare',
      'CareSource', 'Medical Mutual',
    ],
    'New Albany': [
      'Aetna', 'Anthem Indiana (or other Anthem/BCBS)',
      'Cigna', 'United Healthcare',
      'CareSource', 'Medical Mutual',
    ],
    Bloomington: ['Aetna', 'Anthem Indiana (or other Anthem/BCBS)', 'Cigna', 'CareSource', 'Medical Mutual'],
  },
  ky: {
    Louisville: [
      'Aetna', 'Anthem Kentucky (or other Anthem/BCBS)',
      'Cigna', 'United Healthcare',
      'Medicare', 'Medical Mutual',
    ],
    Lexington: [
      'Aetna', 'Anthem Kentucky (or other Anthem/BCBS)',
      'Cigna', 'United Healthcare',
      'Medicare', 'Medical Mutual',
    ],
    Winchester: ['Aetna', 'Anthem Kentucky (or other Anthem/BCBS)', 'Cigna', 'Medical Mutual'],
    'Bowling Green': ['Aetna', 'Anthem Kentucky (or other Anthem/BCBS)', 'Cigna', 'Medical Mutual'],
    Paducah: ['Aetna', 'Anthem Kentucky (or other Anthem/BCBS)', 'Cigna', 'United Healthcare', 'Medical Mutual'],
    Ashland: ['Aetna', 'Anthem Kentucky (or other Anthem/BCBS)', 'Cigna', 'Medical Mutual'],
  },
  la: {
    'New Orleans': ['Aetna', 'Cigna'],
    Shreveport: ['Aetna', 'Cigna', 'United Healthcare'],
    'Baton Rouge': ['Aetna', 'Cigna'],
  },
  ma: {
    Boston: [
      'Aetna', 'Cigna',
      'United Healthcare', 'Medicare',
      'Medical Mutual', 'Harvard Pilgrim Health Plan',
      'Tufts Health Plan', 'Mass General Brigham Health Plan',
    ],
    Springfield: [
      'Aetna', 'Cigna',
      'United Healthcare', 'Medical Mutual',
      'Harvard Pilgrim Health Plan', 'Tufts Health Plan',
      'Mass General Brigham Health Plan',
    ],
    Quincy: [
      'Aetna', 'Cigna',
      'United Healthcare', 'Medical Mutual',
      'Tufts Health Plan', 'Harvard Pilgrim Health Plan',
      'Mass General Brigham Health Plan',
    ],
    Arlington: [
      'Aetna', 'Cigna',
      'United Healthcare', 'Harvard Pilgrim Health Plan',
      'Medical Mutual', 'Tufts Health Plan',
      'Mass General Brigham Health Plan',
    ],
    Worcester: ['Aetna', 'Cigna', 'Medical Mutual', 'Tufts Health Plan', 'Harvard Pilgrim Health Plan'],
    Agawam: ['Aetna', 'Cigna', 'Medical Mutual'],
    'North Andover': ['Aetna', 'Cigna', 'Medical Mutual', 'Harvard Pilgrim Health Plan', 'Tufts Health Plan'],
    Westfield: ['Aetna', 'Cigna', 'Medical Mutual', 'Harvard Pilgrim Health Plan', 'Tufts Health Plan'],
    Amesbury: ['Aetna', 'Cigna', 'United Healthcare', 'Harvard Pilgrim Health Plan', 'Medical Mutual', 'Tufts Health Plan'],
    Haverhill: ['Aetna', 'Cigna', 'Medical Mutual'],
  },
  md: {
    Baltimore: [
      'Aetna', 'CareFirst Maryland (or other BCBS)',
      'Cigna', 'Kaiser Permanente',
      'United Healthcare', 'Medicare',
      'Medical Mutual',
    ],
    Laurel: [
      'Aetna', 'CareFirst Maryland (or other BCBS)',
      'Cigna', 'Kaiser Permanente',
      'United Healthcare', 'Medicare',
      'Medical Mutual',
    ],
    'Silver Spring': [
      'Aetna', 'CareFirst Maryland (or other BCBS)',
      'Cigna', 'Kaiser Permanente',
      'United Healthcare', 'Medicare',
      'Medical Mutual',
    ],
    Frederick: [
      'Aetna', 'CareFirst Maryland (or other BCBS)',
      'Cigna', 'Kaiser Permanente',
      'United Healthcare', 'Medicare',
      'Medical Mutual',
    ],
    Columbia: [
      'Aetna', 'CareFirst Maryland (or other BCBS)',
      'Cigna', 'Kaiser Permanente',
      'United Healthcare', 'Medicare',
      'Medical Mutual',
    ],
    Bowie: [
      'Aetna', 'CareFirst Maryland (or other BCBS)',
      'Cigna', 'Kaiser Permanente',
      'United Healthcare', 'Medical Mutual',
    ],
    Hagerstown: [
      'Aetna', 'CareFirst Maryland (or other BCBS)',
      'Cigna', 'Kaiser Permanente',
      'United Healthcare', 'Medical Mutual',
    ],
    'Upper Marlboro': [
      'Aetna', 'CareFirst Maryland (or other BCBS)',
      'Cigna', 'Kaiser Permanente',
      'United Healthcare', 'Medical Mutual',
    ],
    'Bel Air': [
      'Aetna', 'CareFirst Maryland (or other BCBS)',
      'Cigna', 'Kaiser Permanente',
      'United Healthcare', 'Medical Mutual',
    ],
    Centreville: [
      'Aetna', 'CareFirst Maryland (or other BCBS)',
      'Cigna', 'Kaiser Permanente',
      'United Healthcare', 'Medicare',
      'Medical Mutual',
    ],
  },
  mi: {
    'Grand Rapids': ['Aetna', 'Cigna', 'United Healthcare', 'Paramount', 'Priority Health'],
    Southfield: ['Aetna', 'Cigna', 'Paramount', 'Priority Health'],
    Ypsilanti: ['Aetna', 'Cigna', 'Paramount', 'Priority Health'],
    Detroit: ['Aetna', 'Cigna', 'Paramount'],
    Plymouth: ['Aetna', 'Cigna', 'Paramount', 'Priority Health'],
  },
  mo: {
    'Saint Louis': [
      'Aetna', 'Anthem Missouri (or other Anthem/BCBS)',
      'Cigna', 'United Healthcare',
      'Medicare', 'Medical Mutual',
      'Medica', 'Oscar Health Plan',
      'Tricare Prime Active Duty HMO', 'Ambetter Home State Health',
    ],
    'Kansas City': [
      'Aetna', 'Anthem Missouri (or other Anthem/BCBS)',
      'Cigna', 'United Healthcare',
      'Medicare', 'Medical Mutual',
      'Oscar Health Plan', 'Tricare Prime Active Duty HMO',
      'Ambetter Home State Health', 'Medica',
    ],
    Springfield: [
      'Aetna', 'Anthem Missouri (or other Anthem/BCBS)',
      'Cigna', 'United Healthcare',
      'Medical Mutual', 'Medica',
      'Oscar Health Plan', 'Tricare Prime Active Duty HMO',
      'Ambetter Home State Health',
    ],
    'Jefferson City': [
      'Aetna', 'Anthem Missouri (or other Anthem/BCBS)',
      'Cigna', 'United Healthcare',
      'Medicare', 'Medical Mutual',
      'Medica', 'Oscar Health Plan',
      'Tricare Prime Active Duty HMO', 'Ambetter Home State Health',
    ],
    'Blue Springs': [
      'Aetna', 'Anthem Missouri (or other Anthem/BCBS)',
      'Cigna', 'United Healthcare',
      'Medical Mutual', 'Medica',
      'Oscar Health Plan', 'Tricare Prime Active Duty HMO',
    ],
    Florissant: [
      'Aetna', 'Anthem Missouri (or other Anthem/BCBS)',
      'Cigna', 'United Healthcare',
      'Medical Mutual', 'Oscar Health Plan',
      'Tricare Prime Active Duty HMO', 'Medica',
    ],
    Kirkwood: [
      'Aetna', 'Anthem Missouri (or other Anthem/BCBS)',
      'Cigna', 'United Healthcare',
      'Medical Mutual', 'Medica',
      'Oscar Health Plan', 'Tricare Prime Active Duty HMO',
      'Ambetter Home State Health',
    ],
    'Saint Charles': [
      'Aetna', 'Anthem Missouri (or other Anthem/BCBS)',
      'Cigna', 'United Healthcare',
      'Medical Mutual', 'Oscar Health Plan',
      'Tricare Prime Active Duty HMO', 'Ambetter Home State Health',
      'Medica',
    ],
    Columbia: [
      'Aetna', 'Anthem Missouri (or other Anthem/BCBS)',
      'Cigna', 'United Healthcare',
      'Medica', 'Medical Mutual',
      'Oscar Health Plan', 'Tricare Prime Active Duty HMO',
    ],
    Hazelwood: ['Aetna', 'Anthem Missouri (or other Anthem/BCBS)', 'Cigna', 'United Healthcare', 'Medical Mutual', 'Medica', 'Oscar Health Plan', 'Tricare Prime Active Duty HMO', 'Prime Health'],
  },
  nc: {
    Charlotte: [
      'Aetna', 'Blue Cross Blue Shield North Carolina (or other BCBS plan)',
      'Cigna', 'United Healthcare',
      'Medicare', 'Oscar',
    ],
    Raleigh: [
      'Aetna', 'Blue Cross Blue Shield North Carolina (or other BCBS plan)',
      'Cigna', 'United Healthcare',
      'Medicare', 'Oscar',
    ],
    Fayetteville: [
      'Aetna', 'Blue Cross Blue Shield North Carolina (or other BCBS plan)',
      'Cigna', 'United Healthcare',
      'Oscar',
    ],
    Greensboro: [
      'Aetna', 'Blue Cross Blue Shield North Carolina (or other BCBS plan)',
      'Cigna', 'United Healthcare',
      'Oscar',
    ],
    Durham: ['Aetna', 'Blue Cross Blue Shield North Carolina (or other BCBS plan)', 'Cigna', 'United Healthcare', 'Oscar', 'ClaimDOC', 'Partners Direct'],
    Asheville: ['Aetna', 'Blue Cross Blue Shield North Carolina (or other BCBS plan)', 'Cigna'],
    Greenville: ['Aetna', 'Blue Cross Blue Shield North Carolina (or other BCBS plan)', 'Cigna'],
    Cary: ['Aetna', 'Cigna'],
    Mooresville: ['Aetna', 'Cigna'],
  },
  ne: {
    Omaha: ['Aetna', 'BCBS Nebraska (or other BCBS)', 'Cigna', 'United Healthcare', 'Medical Mutual', 'Oscar Health Plan', 'Medica', 'Tricare Prime Active Duty HMO', 'Partners Direct', 'Prime Health'],
    Lincoln: [
      'Aetna', 'BCBS Nebraska (or other BCBS)',
      'Cigna', 'United Healthcare',
      'Medical Mutual', 'Oscar Health Plan',
      'Tricare Prime Active Duty HMO', 'Medica',
    ],
  },
  nj: {
    'Toms River': ['Aetna', 'Cigna', 'United Healthcare', 'Oscar Health Plan', 'ClaimDOC'],
    Newark: ['Aetna', 'Cigna'],
  },
  nm: {
    Albuquerque: ['Aetna', 'BCBS New Mexico (or other BCBS)', 'Cigna', 'United Healthcare', 'Presbyterian Health Plan', 'Prime Health'],
    'Rio Rancho': ['Aetna', 'BCBS New Mexico (or other BCBS)', 'Cigna'],
  },
  nv: {
    'Las Vegas': ['Aetna', 'Cigna', 'United Healthcare', 'Medicare', 'Imperial Health Plans', 'Tricare Prime Active Duty HMO', 'Hometown Health', 'Aetna Allied Benefit System', 'Aetna Meritain', 'Partners Direct'],
    Henderson: ['Aetna', 'Cigna', 'United Healthcare', 'Imperial Health Plans', 'Tricare Prime Active Duty HMO'],
    'North Las Vegas': ['Aetna', 'Cigna', 'Imperial Health Plans'],
  },
  ny: {
    Brooklyn: ['Aetna', 'Cigna', 'United Healthcare', 'Oscar Health Plan', 'MVP Healthcare'],
    'New York': ['Aetna', 'Cigna', 'United Healthcare', 'Oscar Health Plan', 'MVP Healthcare', 'ClaimDOC', 'Unity Preferred Network'],
    Bronx: ['Aetna', 'Cigna', 'United Healthcare', 'Oscar Health Plan'],
    Rochester: ['Aetna', 'Cigna'],
    'Staten Island': ['Aetna', 'Cigna', 'United Healthcare', 'Oscar Health Plan'],
    'Valley Stream': ['Aetna', 'Cigna', 'United Healthcare', 'Oscar Health Plan'],
    Buffalo: ['Aetna', 'Cigna'],
    Yonkers: ['Aetna', 'Cigna'],
  },
  oh: {
    Columbus: [
      'Aetna', 'Anthem Ohio (or other Anthem/BCBS)',
      'Cigna', 'United Healthcare',
      'Medicare', 'CareSource',
      'OhioHealthy', 'Paramount',
      'The Health Plan', 'Medical Mutual of Ohio',
    ],
    Cincinnati: [
      'Aetna', 'Anthem Ohio (or other Anthem/BCBS)',
      'Cigna', 'United Healthcare',
      'Medicare', 'CareSource',
      'OhioHealthy', 'Paramount',
      'The Health Plan', 'Medical Mutual of Ohio',
    ],
    Canton: [
      'Aetna', 'Anthem Ohio (or other Anthem/BCBS)',
      'Cigna', 'United Healthcare',
      'Medicare', 'CareSource',
      'OhioHealthy', 'Paramount',
      'The Health Plan', 'Medical Mutual of Ohio',
    ],
    Dayton: [
      'Aetna', 'Anthem Ohio (or other Anthem/BCBS)',
      'Cigna', 'United Healthcare',
      'Medicare', 'CareSource',
      'OhioHealthy', 'Paramount',
      'The Health Plan', 'Medical Mutual of Ohio',
    ],
    Akron: [
      'Aetna', 'Anthem Ohio (or other Anthem/BCBS)',
      'Cigna', 'United Healthcare',
      'CareSource', 'OhioHealthy',
      'Paramount', 'The Health Plan',
      'Medical Mutual of Ohio', 'Oscar Health Plan',
    ],
    Cleveland: [
      'Aetna', 'Anthem Ohio (or other Anthem/BCBS)',
      'Cigna', 'United Healthcare',
      'CareSource', 'Medical Mutual of Ohio',
      'OhioHealthy', 'Paramount',
      'The Health Plan', 'Oscar Health Plan',
    ],
    Toledo: [
      'Aetna', 'Anthem Ohio (or other Anthem/BCBS)',
      'Cigna', 'United Healthcare',
      'Medical Mutual of Ohio', 'OhioHealthy',
      'Paramount', 'The Health Plan',
      'CareSource', 'Oscar Health Plan',
    ],
    'North Canton': [
      'Aetna', 'Anthem Ohio (or other Anthem/BCBS)',
      'Cigna', 'United Healthcare',
      'CareSource', 'OhioHealthy',
      'Paramount', 'The Health Plan',
      'Medical Mutual of Ohio', 'Oscar Health Plan',
    ],
    Delaware: [
      'Aetna', 'Anthem Ohio (or other Anthem/BCBS)',
      'Cigna', 'United Healthcare',
      'CareSource', 'Medical Mutual of Ohio',
      'OhioHealthy', 'Paramount',
      'The Health Plan', 'Oscar Health Plan',
    ],
    Youngstown: [
      'Aetna', 'Anthem Ohio (or other Anthem/BCBS)',
      'Cigna', 'United Healthcare',
      'Medical Mutual of Ohio', 'OhioHealthy',
      'Paramount', 'The Health Plan',
      'CareSource', 'Oscar Health Plan',
    ],
  },
  ok: {
    Tulsa: [
      'Aetna', 'BCBS Oklahoma (or other BCBS)',
      'Cigna', 'United Healthcare',
      'Medica', 'Oscar Health Plan',
      'Tricare Prime Active Duty HMO',
    ],
    'Oklahoma City': ['Aetna', 'BCBS Oklahoma (or other BCBS)', 'Cigna', 'United Healthcare', 'Medica', 'Oscar Health Plan', 'Alliance Coal Health Plan', 'Imagine Health'],
  },
  or: {
    Portland: ['Aetna', 'Regence of Oregon (or other BCBS)', 'Cigna', 'United Healthcare', 'Moda Marketplace Health Plans', 'Tricare Prime Active Duty HMO', 'First Choice Health'],
    Salem: ['Aetna', 'Cigna', 'Moda Marketplace Health Plans'],
    Eugene: ['Aetna', 'Regence of Oregon (or other BCBS)', 'Cigna', 'United Healthcare', 'Moda Marketplace Health Plans'],
  },
  pa: {
    Philadelphia: ['Aetna', 'Capital Blue Cross (or other BCBS plan)', 'Highmark (or other BCBS plan)', 'Cigna', 'United Healthcare', 'Medicare', 'The Health Plan', 'Oscar', 'Partners Direct', 'Prime Health'],
    Pittsburgh: [
      'Aetna', 'Capital Blue Cross (or other BCBS plan)',
      'Highmark (or other BCBS plan)', 'Cigna',
      'The Health Plan',
    ],
    Reading: [
      'Aetna', 'Capital Blue Cross (or other BCBS plan)',
      'Highmark (or other BCBS plan)', 'Cigna',
      'The Health Plan',
    ],
  },
  sc: {
    Columbia: ['Aetna', 'Cigna', 'United Healthcare', 'Medicare'],
    'Myrtle Beach': ['Aetna', 'Cigna'],
    Summerville: ['Aetna', 'Cigna'],
  },
  tn: {
    Chattanooga: ['Aetna', 'Blue Cross Blue Shield Tennessee (or other BCBS plan)', 'Cigna'],
    Memphis: ['Aetna', 'Blue Cross Blue Shield Tennessee (or other BCBS plan)', 'Cigna', 'United Healthcare', 'Oscar', 'ClaimDOC'],
    Cordova: ['Aetna', 'Cigna'],
    Nashville: ['Aetna', 'Cigna', 'ClaimDOC', 'Partners Direct'],
  },
  tx: {
    Houston: [
      'Aetna', 'Blue Cross Blue Shield of Texas PPO (or other BCBS plan)',
      'Cigna', 'United Healthcare',
      'Medicare', 'HealthPartners',
      'Medical Mutual', 'Moda Health',
      'Imperial Health Plans', 'Molina Healthcare',
    ],
    'San Antonio': [
      'Aetna', 'Blue Cross Blue Shield of Texas PPO (or other BCBS plan)',
      'Cigna', 'United Healthcare',
      'Medicare', 'Imperial Health Plans',
      'Medical Mutual', 'Moda Health',
      'HealthPartners', 'Ambetter Superior HealthPlan',
    ],
    Austin: [
      'Aetna', 'Blue Cross Blue Shield of Texas PPO (or other BCBS plan)',
      'Cigna', 'United Healthcare',
      'Medicare', 'HealthPartners',
      'Medical Mutual', 'Moda Health',
      'Imperial Health Plans', 'Ambetter Superior HealthPlan',
    ],
    Dallas: [
      'Aetna', 'Blue Cross Blue Shield of Texas PPO (or other BCBS plan)',
      'Cigna', 'United Healthcare',
      'Medicare', 'Imperial Health Plans',
      'Moda Health', 'HealthPartners',
      'Medical Mutual', 'Molina Healthcare',
    ],
    'Fort Worth': [
      'Aetna', 'Blue Cross Blue Shield of Texas PPO (or other BCBS plan)',
      'Cigna', 'United Healthcare',
      'Medicare', 'HealthPartners',
      'Imperial Health Plans', 'Medical Mutual',
      'Moda Health', 'Ambetter Superior HealthPlan',
    ],
    'El Paso': [
      'Aetna', 'Blue Cross Blue Shield of Texas PPO (or other BCBS plan)',
      'Cigna', 'United Healthcare',
      'Medicare', 'HealthPartners',
      'Imperial Health Plans', 'Medical Mutual',
      'Moda Health', 'Ambetter Superior HealthPlan',
    ],
    Katy: [
      'Aetna', 'Blue Cross Blue Shield of Texas PPO (or other BCBS plan)',
      'Cigna', 'United Healthcare',
      'Medicare', 'HealthPartners',
      'Imperial Health Plans', 'Medical Mutual',
      'Moda Health', 'Ambetter Superior HealthPlan',
    ],
    Spring: [
      'Aetna', 'Blue Cross Blue Shield of Texas PPO (or other BCBS plan)',
      'Cigna', 'United Healthcare',
      'Medicare', 'HealthPartners',
      'Imperial Health Plans', 'Medical Mutual',
      'Moda Health', 'Ambetter Superior HealthPlan',
    ],
    Arlington: [
      'Aetna', 'Blue Cross Blue Shield of Texas PPO (or other BCBS plan)',
      'Cigna', 'United Healthcare',
      'Medicare', 'Imperial Health Plans',
      'Medical Mutual', 'Moda Health',
      'Ambetter Superior HealthPlan', 'HealthPartners',
    ],
    Amarillo: [
      'Aetna', 'Blue Cross Blue Shield of Texas PPO (or other BCBS plan)',
      'Cigna', 'United Healthcare',
      'Medicare', 'HealthPartners',
      'Imperial Health Plans', 'Medical Mutual',
      'Moda Health', 'Ambetter Superior HealthPlan',
    ],
  },
  ut: {
    'Salt Lake City': ['Aetna', 'Cigna', 'United Healthcare', 'Imperial Health Plans', 'Select Health', 'Tricare Prime Active Duty HMO', 'Sana Benefits'],
    Provo: ['Aetna', 'Cigna', 'Imperial Health Plans', 'Select Health'],
  },
  va: {
    'Virginia Beach': [
      'Aetna', 'Anthem Blue Cross Blue Shield Virginia (or other BCBS plan)',
      'CareFirst Blue Cross Blue Shield (or other BCBS plan)', 'Cigna',
      'Kaiser Permanente', 'United Healthcare',
      'Medicare', 'Medical Mutual',
      'Oscar', 'Sentara Health Plans',
    ],
    Richmond: [
      'Aetna', 'Anthem Blue Cross Blue Shield Virginia (or other BCBS plan)',
      'CareFirst Blue Cross Blue Shield (or other BCBS plan)', 'Cigna',
      'Kaiser Permanente', 'United Healthcare',
      'Medicare', 'Medical Mutual',
      'Oscar', 'Sentara Health Plans',
    ],
    Alexandria: [
      'Aetna', 'Anthem Blue Cross Blue Shield Virginia (or other BCBS plan)',
      'CareFirst Blue Cross Blue Shield (or other BCBS plan)', 'Cigna',
      'Kaiser Permanente', 'United Healthcare',
      'Medicare', 'Medical Mutual',
      'Oscar', 'Sentara Health Plans',
    ],
    Roanoke: [
      'Aetna', 'Anthem Blue Cross Blue Shield Virginia (or other BCBS plan)',
      'CareFirst Blue Cross Blue Shield (or other BCBS plan)', 'Cigna',
      'Kaiser Permanente', 'United Healthcare',
      'Medicare', 'Medical Mutual',
      'Oscar', 'Sentara Health Plans',
    ],
    Midlothian: [
      'Aetna', 'Anthem Blue Cross Blue Shield Virginia (or other BCBS plan)',
      'CareFirst Blue Cross Blue Shield (or other BCBS plan)', 'Cigna',
      'Kaiser Permanente', 'United Healthcare',
      'Medicare', 'Medical Mutual',
      'Oscar', 'Sentara Health Plans',
    ],
    Chesapeake: [
      'Aetna', 'Anthem Blue Cross Blue Shield Virginia (or other BCBS plan)',
      'CareFirst Blue Cross Blue Shield (or other BCBS plan)', 'Cigna',
      'Kaiser Permanente', 'United Healthcare',
      'Medical Mutual', 'Oscar',
      'Sentara Health Plans', 'Sentara Medicaid',
    ],
    Hampton: [
      'Aetna', 'Anthem Blue Cross Blue Shield Virginia (or other BCBS plan)',
      'CareFirst Blue Cross Blue Shield (or other BCBS plan)', 'Cigna',
      'Kaiser Permanente', 'United Healthcare',
      'Medicare', 'Medical Mutual',
      'Sentara Health Plans', 'Oscar',
    ],
    Henrico: [
      'Aetna', 'Anthem Blue Cross Blue Shield Virginia (or other BCBS plan)',
      'CareFirst Blue Cross Blue Shield (or other BCBS plan)', 'Cigna',
      'Kaiser Permanente', 'United Healthcare',
      'Medicare', 'Medical Mutual',
      'Sentara Health Plans', 'Oscar',
    ],
    Fredericksburg: [
      'Aetna', 'Anthem Blue Cross Blue Shield Virginia (or other BCBS plan)',
      'CareFirst Blue Cross Blue Shield (or other BCBS plan)', 'Cigna',
      'Kaiser Permanente', 'United Healthcare',
      'Medical Mutual', 'Oscar',
      'Sentara Health Plans',
    ],
    Lynchburg: [
      'Aetna', 'Anthem Blue Cross Blue Shield Virginia (or other BCBS plan)',
      'CareFirst Blue Cross Blue Shield (or other BCBS plan)', 'Cigna',
      'Kaiser Permanente', 'United Healthcare',
      'Medical Mutual', 'Sentara Health Plans',
      'Oscar', 'Sentara Medicaid',
    ],
  },
  wa: {
    Seattle: [
      'Aetna', 'Premera of Washington (or other BCBS)',
      'Regence of Washington (or other BCBS)', 'Cigna',
      'Moda Marketplace Health Plans',
    ],
    Olympia: ['Aetna', 'Premera of Washington (or other BCBS)', 'Cigna', 'Moda Marketplace Health Plans'],
    Kirkland: ['Aetna', 'Cigna', 'Moda Marketplace Health Plans'],
    Marysville: ['Aetna', 'Cigna', 'Moda Marketplace Health Plans'],
  },
  wi: {
    Milwaukee: ['Aetna', 'Cigna', 'United Healthcare', 'Network Health', 'Medica', 'Tricare Prime Active Duty HMO'],
    Madison: ['Aetna', 'Cigna', 'Network Health', 'Sana Benefits'],
    Appleton: ['Aetna', 'Cigna', 'Network Health', 'Sana Benefits'],
    'Green Bay': ['Aetna', 'Cigna', 'Network Health'],
  },
};

// Top cities per state by provider count.
export const INTERNAL_LINK_STATE_CITIES: CityNamesByStateSlug = {
  al: ['Montgomery', 'Birmingham', 'Huntsville', 'Mobile'],
  ar: ['Jonesboro', 'Little Rock'],
  az: [
    'Phoenix', 'Tucson', 'Mesa', 'Chandler',
    'Scottsdale', 'Surprise', 'Flagstaff', 'Peoria',
    'Tempe', 'Gilbert',
  ],
  ca: [
    'San Diego', 'Los Angeles', 'Oakland', 'Long Beach',
    'Sacramento', 'Roseville', 'Corona', 'Fresno',
    'Irvine', 'Riverside',
  ],
  co: [
    'Denver', 'Colorado Springs', 'Aurora', 'Boulder',
    'Fort Collins', 'Lakewood', 'Littleton', 'Thornton',
    'Centennial', 'Broomfield',
  ],
  dc: ['Washington'],
  de: ['Wilmington'],
  fl: [
    'Miami', 'Jacksonville', 'Tampa', 'Fort Lauderdale',
    'West Palm Beach', 'Orlando', 'Boynton Beach', 'Hollywood',
    'Miramar', 'Boca Raton',
  ],
  ga: [
    'Atlanta', 'Marietta', 'Lawrenceville', 'Mcdonough',
    'Decatur', 'Douglasville', 'Snellville', 'Stone Mountain',
    'Savannah', 'Conyers',
  ],
  hi: ['Honolulu'],
  il: ['Chicago', 'Evanston', 'Oak Park'],
  in: [
    'Indianapolis', 'Fort Wayne', 'Evansville', 'Lafayette',
    'South Bend', 'Valparaiso', 'Carmel', 'Jeffersonville',
    'New Albany', 'Bloomington',
  ],
  ky: ['Louisville', 'Lexington', 'Winchester', 'Bowling Green', 'Paducah', 'Ashland', 'Covington', 'Florence'],
  la: ['New Orleans', 'Shreveport', 'Baton Rouge', 'Alexandria', 'Lafayette'],
  ma: [
    'Boston', 'Springfield', 'Quincy', 'Arlington',
    'Worcester', 'Agawam', 'North Andover', 'Westfield',
    'Amesbury', 'Haverhill',
  ],
  md: [
    'Baltimore', 'Laurel', 'Silver Spring', 'Frederick',
    'Columbia', 'Bowie', 'Hagerstown', 'Upper Marlboro',
    'Bel Air', 'Centreville',
  ],
  mi: ['Grand Rapids', 'Southfield', 'Ypsilanti', 'Detroit', 'Kalamazoo', 'Plymouth'],
  mo: [
    'Saint Louis', 'Kansas City', 'Springfield', 'Jefferson City',
    'Blue Springs', 'Florissant', 'Kirkwood', 'Saint Charles',
    'Columbia', 'Hazelwood',
  ],
  ms: ['Ocean Springs'],
  nc: [
    'Charlotte', 'Raleigh', 'Fayetteville', 'Greensboro',
    'Durham', 'Asheville', 'Greenville', 'Cary',
    'Mooresville', 'Wilmington',
  ],
  ne: ['Omaha', 'Lincoln'],
  nj: ['Toms River', 'Newark'],
  nm: ['Albuquerque', 'Santa Fe', 'Rio Rancho', 'Las Cruces'],
  nv: ['Las Vegas', 'Henderson', 'North Las Vegas', 'Reno'],
  ny: [
    'Brooklyn', 'New York', 'Bronx', 'Rochester',
    'Staten Island', 'Valley Stream', 'Buffalo', 'Hamburg',
    'Jamaica', 'Yonkers',
  ],
  oh: [
    'Columbus', 'Cincinnati', 'Canton', 'Dayton',
    'Akron', 'Cleveland', 'Toledo', 'North Canton',
    'Delaware', 'Youngstown',
  ],
  ok: ['Tulsa', 'Oklahoma City', 'Edmond'],
  or: ['Portland', 'Salem', 'Eugene', 'Bend'],
  pa: ['Philadelphia', 'Pittsburgh', 'Reading', 'Altoona'],
  ri: ['Providence'],
  sc: ['Columbia', 'Myrtle Beach', 'Summerville', 'Charleston', 'North Augusta'],
  tn: ['Chattanooga', 'Clarksville', 'Memphis', 'Cordova', 'Nashville'],
  tx: [
    'Houston', 'San Antonio', 'Austin', 'Dallas',
    'Fort Worth', 'El Paso', 'Katy', 'Spring',
    'Arlington', 'Amarillo',
  ],
  ut: ['Salt Lake City', 'Provo'],
  va: [
    'Virginia Beach', 'Richmond', 'Alexandria', 'Roanoke',
    'Midlothian', 'Chesapeake', 'Hampton', 'Henrico',
    'Fredericksburg', 'Lynchburg',
  ],
  wa: ['Seattle', 'Olympia', 'Kirkland', 'Marysville'],
  wi: ['Milwaukee', 'Madison', 'Appleton', 'Green Bay'],
  wy: ['Cheyenne'],
};
